import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import api from "../services/api"; // ✅ adjust path if needed

/* What a product is measured and priced in.
 *
 * The short code is what gets stored and what prints on an invoice; the words
 * beside it are only so the right one is easy to pick. GAL is here because
 * waste oil is bought and sold by the gallon, not by weight. EA was already
 * in use on a product but missing from this list, which left that product's
 * unit box blank whenever it was edited. */
const UNIT_OPTIONS = [
  { code: "LB",  label: "LB — Pounds" },
  { code: "KG",  label: "KG — Kilograms" },
  { code: "MT",  label: "MT — Metric tons" },
  { code: "GAL", label: "GAL — Gallons" },
  { code: "EA",  label: "EA — Each / piece" },
];

/* "Description_ES" is not a label anybody should have to read. */
const FIELD_LABELS = {
  Product_Description: "Description",
  Description_ES: "Spanish name (for the yard crew)",
  Opening_Qty: "Opening Qty",
};

/* Older products carry units typed by hand ("Lb.", and so on). A select whose
 * value is not one of its options renders empty, which reads as "no unit set"
 * on a product that has one. So keep whatever is already stored in the list. */
function unitOptions(current) {
  const has = UNIT_OPTIONS.some((u) => u.code === current);
  return has || !current
    ? UNIT_OPTIONS
    : [...UNIT_OPTIONS, { code: current, label: current }];
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({
    Product_Description: "",
    Opening_Qty: "",
    Unit: "",
    Description_ES: "",
    Date: "",
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");

  /* ================= FETCH ================= */
  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data);
      setFilteredProducts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  /* ================= SEARCH ================= */
  const handleSearch = (value) => {
    setSearchQuery(value);
    const filtered = products.filter((p) =>
      Object.values(p)
        .map((v) => v || "")
        .join(" ")
        .toLowerCase()
        .includes(value.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  /* ================= CREATE ================= */
  const handleCreate = async () => {
    if (!newProduct.Product_Description || !newProduct.Unit || !newProduct.Date) {
      alert("Please fill all fields");
      return;
    }

    try {
      await api.post("/products", {
        ...newProduct,
        Opening_Qty: Number(newProduct.Opening_Qty),
      });

      setNewProduct({
        Product_Description: "",
        Opening_Qty: "",
        Unit: "",
        Description_ES: "",
        Date: "",
      });
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.error || "Error creating product");
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    try {
      await api.put(`/products/${selectedProduct.Item_ID}`, {
        ...selectedProduct,
        Opening_Qty: Number(selectedProduct.Opening_Qty),
      });
      setEditDialogOpen(false);
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Product Management
      </Typography>

      {/* ADD PRODUCT */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6">Add New Product</Typography>
        <Grid container spacing={2} mt={1}>
          {["Product_Description", "Description_ES", "Opening_Qty", "Unit", "Date"].map((field) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={field}>
              <TextField
                label={FIELD_LABELS[field] || field.replace("_", " ")}
                type={field === "Date" ? "date" : "text"}
                select={field === "Unit"}
                value={newProduct[field]}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, [field]: e.target.value })
                }
                InputLabelProps={field === "Date" ? { shrink: true } : {}}
                fullWidth
              >
                {field === "Unit" && unitOptions(newProduct.Unit).map((u) => (
                  <MenuItem key={u.code} value={u.code}>{u.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
        </Grid>
        <Button variant="contained" sx={{ mt: 2 }} onClick={handleCreate}>
          Add Product
        </Button>
      </Paper>

      {/* SEARCH */}
      <TextField
        label="Search"
        fullWidth
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* LIST */}
      {isMobile ? (
        filteredProducts.map((p) => (
          <Paper key={p.Item_ID} sx={{ p: 2, mb: 2 }}>
            <Typography><b>ID:</b> {p.Item_ID}</Typography>
            <Typography><b>Description:</b> {p.Product_Description}</Typography>
            <Typography><b>Spanish:</b> {p.Description_ES || "—"}</Typography>
            <Typography><b>Qty:</b> {p.Opening_Qty}</Typography>
            <Typography><b>Unit:</b> {p.Unit}</Typography>
            <Typography><b>Date:</b> {p.Date}</Typography>
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => {
                setSelectedProduct(p);
                setEditDialogOpen(true);
              }}>
                Edit
              </Button>
              <Button size="small" color="error" onClick={() => handleDelete(p.Item_ID)}>
                Delete
              </Button>
            </Box>
          </Paper>
        ))
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "#eee" }}>
                {["ID", "Description", "Spanish", "Qty", "Unit", "Date", "Actions"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((p) => (
                <TableRow key={p.Item_ID}>
                  <TableCell>{p.Item_ID}</TableCell>
                  <TableCell>{p.Product_Description}</TableCell>
                  <TableCell sx={{ color: p.Description_ES ? "inherit" : "text.disabled" }}>
                    {p.Description_ES || "—"}
                  </TableCell>
                  <TableCell>{p.Opening_Qty}</TableCell>
                  <TableCell>{p.Unit}</TableCell>
                  <TableCell>{p.Date}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => {
                      setSelectedProduct(p);
                      setEditDialogOpen(true);
                    }}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDelete(p.Item_ID)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          {selectedProduct &&
            ["Product_Description", "Description_ES", "Opening_Qty", "Unit", "Date"].map((field) => (
              <TextField
                key={field}
                fullWidth
                label={FIELD_LABELS[field] || field.replace("_", " ")}
                type={field === "Date" ? "date" : "text"}
                select={field === "Unit"}
                value={selectedProduct[field] || ""}
                onChange={(e) =>
                  setSelectedProduct({ ...selectedProduct, [field]: e.target.value })
                }
                sx={{ mb: 2 }}
                InputLabelProps={field === "Date" ? { shrink: true } : {}}
              >
                {field === "Unit" && unitOptions(selectedProduct.Unit).map((u) => (
                  <MenuItem key={u.code} value={u.code}>{u.label}</MenuItem>
                ))}
              </TextField>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
